import { Flex } from 'src/styles/flexComponent'
import { ROVFolder } from './ROVFolder'
import { ROVVideoComponent } from './ROVVideoComponent'
import { Box, styled, Typography, useTheme } from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import { EmptyStatusCard } from './mooring/EmptyStatusCard'
import { IROVVideoCategory, IROVVideoMinimal } from 'src/generated-types/rovvideo'
import { convertToPascalCase } from 'src/utils/function'
import { useTranslation } from 'react-i18next'
import { GridActionButton } from './common/GridActionButton'

interface ROVFolderContainerProps {
  selectedCategory: EntityId | null
  handleCategorySelect: (categoryId: EntityId | null) => void
  categoriesMap: Record<EntityId, { videos: IROVVideoMinimal[]; category: IROVVideoCategory }>
  customerId?: EntityId | null
  localityId?: EntityId | null
  mooringId?: EntityId | null
  positionId?: EntityId | null
  mainComponentId?: EntityId | null
  allCategories: [EntityId, { videos: IROVVideoMinimal[]; category: IROVVideoCategory }][]
  videosWithoutCategory: IROVVideoMinimal[]
  availableCategories: IROVVideoCategory[]
  rowFlex?: string
  height?: string | number
  maxHeight?: string | number
  disabled?: boolean
}

const ROVFolderContainer = ({
  selectedCategory,
  handleCategorySelect,
  categoriesMap,
  customerId,
  localityId,
  mooringId,
  positionId,
  mainComponentId,
  allCategories,
  videosWithoutCategory,
  availableCategories,
  rowFlex,
  height,
  maxHeight,
  disabled,
}: ROVFolderContainerProps) => {
  const { t } = useTranslation(['position'])
  const theme = useTheme()

  return (
    <Flex.Row flexWrap="wrap" className="p-12-core" height={height ?? '100%'} maxHeight={maxHeight} overflow="auto">
      {selectedCategory === null ? (
        <Flex.Column gap={2} width="100%">
          <GridBox>
            {allCategories.map(([, categoryData]) => (
              <ROVFolder
                key={categoryData.category.id}
                categoryName={categoryData.category.categoryName}
                count={categoryData.videos.length}
                category={categoryData.category}
                onClick={() => handleCategorySelect(categoryData.category.id as EntityId)}
                customerId={customerId}
                localityId={localityId}
                mooringId={mooringId}
                positionId={positionId}
                mainComponentId={mainComponentId}
                disabled={disabled}
              />
            ))}
          </GridBox>
          {videosWithoutCategory.length > 0 && (
            <GridBox>
              {videosWithoutCategory.map((video, index) => (
                <ROVVideoComponent
                  key={video.id ?? index}
                  rovvideo={video}
                  width="100%"
                  categories={availableCategories}
                  isShowAction={true}
                  customerId={customerId}
                  localityId={localityId}
                  mooringId={mooringId}
                  positionId={positionId}
                  mainComponentId={mainComponentId}
                  rowFlex={rowFlex}
                  disabled={disabled}
                />
              ))}
            </GridBox>
          )}
        </Flex.Column>
      ) : (
        <Flex.Column width="100%">
          <Flex.Row flexWrap="wrap" justifyContent="start" alignItems="center" mb="8px">
            <GridActionButton
              toolTipTitle={t('back', { ns: 'common' })}
              width="24px"
              height="24px"
              buttonIcon={
                <ArrowBack
                  sx={{
                    fontSize: '14px',
                    color: theme.palette.primary.main,
                  }}
                />
              }
              onClick={() => handleCategorySelect(null)}
              sx={{ marginRight: '8px' }}
            />
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
              {convertToPascalCase(categoriesMap[selectedCategory]?.category.categoryName ?? '')}
            </Typography>
          </Flex.Row>
          {categoriesMap[selectedCategory]?.videos.length > 0 ? (
            <GridBox>
              {categoriesMap[selectedCategory].videos.map((video, index) => (
                <ROVVideoComponent
                  key={video.id ?? index}
                  rovvideo={video}
                  width="100%"
                  categories={availableCategories.filter(
                    category => category.id !== categoriesMap[selectedCategory]?.category.id,
                  )}
                  isShowAction={true}
                  customerId={customerId}
                  localityId={localityId}
                  mooringId={mooringId}
                  positionId={positionId}
                  mainComponentId={mainComponentId}
                  rowFlex={rowFlex}
                  disabled={disabled}
                />
              ))}
            </GridBox>
          ) : (
            <Box height="100%">
              <EmptyStatusCard
                imageWidth="107px"
                header={t('rov-movies-empty-status-title', { ns: 'position' })}
                headerFontSize="18px"
                contentFontSize="14px"
                contentClasses="mt-2"
                contentTopMargin="10px"
                imageContainerClasses="pb-4 pt-4"
                height="100%"
              />
            </Box>
          )}
        </Flex.Column>
      )}
    </Flex.Row>
  )
}

export default ROVFolderContainer

const GridBox = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
  width: '100%',
})
